import React, { useCallback, useState, useEffect, useRef } from "react";
import TruckLoadingScene from "../TruckLoadingScene";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/apiService";

const MultiLineTruckManager = React.forwardRef(function MultiLineTruckManager({
  lines,
  setLines,
  activeLineId,
  setActiveLineId,
  onRowWeightsUpdate, // Add callback prop
  onUnplacedChange, // callback to report total unplaced across lines
  // Optional controlled unplaced state from parent
  unplacedByLine: externalUnplacedByLine,
  setUnplacedByLine: setExternalUnplacedByLine,
  onSelectedPalletChange,
}, ref) {
  const { isAuthenticated, authenticatedFetch } = useAuth();
  const lastAutosaveRef = useRef(null);
  const saveTimerRef = useRef(null);
  const hydratedRef = useRef(false);
  // Local persistence (fallback when unauthenticated or server unavailable)
  const LOCAL_KEY = 'ml_autosave_v1';
  const localHydratedRef = useRef(false);
  // ── Local 3D‐scene state (placed early so callbacks can reference setters) ──
  const [activeTruckIndex, setActiveTruckIndex] = useState(0);

  // Ensure apiService is configured with auth
  useEffect(() => {
    if (isAuthenticated && authenticatedFetch) {
      apiService.setAuthContext({ authenticatedFetch });
    }
  }, [isAuthenticated, authenticatedFetch]);

  // ── Which line is active, and its trucks ──
  const activeLineIndex = lines.findIndex((l) => l.id === activeLineId);
  const truckList = lines[activeLineIndex]?.trucks || [];

  // ── Updater to patch just this line’s trucks ──
  // Accepts either a functional updater (current => next) or a direct array value.
  const updateLineTrucks = useCallback(
    (updaterOrValue) => {
      const isFn = typeof updaterOrValue === 'function';
      setLines((prev) =>
        prev.map((line, i) => {
          if (i !== activeLineIndex) return line;
          const nextTrucks = isFn ? updaterOrValue(line.trucks) : updaterOrValue;
          return { ...line, trucks: nextTrucks };
        })
      );
    },
    [activeLineIndex, setLines]
  );

  // ── Per-line unplaced pallets map ──
  const [internalUnplacedByLine, setInternalUnplacedByLine] = useState(() => {
    const init = {};
    lines.forEach((l) => { init[l.id] = []; });
    return init;
  });
  const unplacedByLine = externalUnplacedByLine ?? internalUnplacedByLine;
  const setUnplacedByLine = setExternalUnplacedByLine ?? setInternalUnplacedByLine;

  // Helper to apply a persisted snapshot safely
  const applySnapshot = useCallback((snap) => {
    if (!snap || typeof snap !== 'object') return;
    const savedLines = snap.ml_lines;
    const savedUnplaced = snap.ml_unplacedByLine;
    const savedActiveLineId = snap.ml_activeLineId;
    const savedActiveTruckIndex = snap.ml_activeTruckIndex;
    if (Array.isArray(savedLines) && savedLines.length > 0) {
      setLines(savedLines);
    }
    if (savedUnplaced && typeof savedUnplaced === 'object') {
      setUnplacedByLine(savedUnplaced);
    }
    if (typeof savedActiveLineId === 'string') {
      setActiveLineId(savedActiveLineId);
    }
    if (typeof savedActiveTruckIndex === 'number') {
      setActiveTruckIndex(savedActiveTruckIndex);
    }
  }, [setLines, setUnplacedByLine, setActiveLineId]);

  // ── Load persisted state from backend autosave when user is authenticated ──
  useEffect(() => {
    let cancelled = false;
    async function loadAutosave() {
      // Wait until auth is ready and authenticatedFetch is configured on apiService
      if (!isAuthenticated) return;
      if (!authenticatedFetch) return;
      // Ensure apiService has auth before we call it (idempotent)
      if (!apiService.authContext) {
        apiService.setAuthContext({ authenticatedFetch });
      }
      if (!apiService.authContext) return;
      try {
        const existing = await apiService.getAutosave();
        lastAutosaveRef.current = existing || {};
        const savedLines = existing?.ml_lines;
        const savedUnplaced = existing?.ml_unplacedByLine;
        const savedActiveLineId = existing?.ml_activeLineId;
        const savedActiveTruckIndex = existing?.ml_activeTruckIndex;

        if (!cancelled) {
          if (Array.isArray(savedLines) && savedLines.length > 0) {
            setLines(savedLines);
          }
          if (savedUnplaced && typeof savedUnplaced === 'object') {
            setUnplacedByLine(savedUnplaced);
          }
          if (typeof savedActiveLineId === 'string') {
            setActiveLineId(savedActiveLineId);
          }
          if (typeof savedActiveTruckIndex === 'number') {
            setActiveTruckIndex(savedActiveTruckIndex);
          }
          // If server returned empty, attempt local fallback
          const serverEmpty = !Array.isArray(savedLines) || savedLines.length === 0;
          if (serverEmpty) {
            try {
              const raw = localStorage.getItem(LOCAL_KEY);
              if (raw) {
                const localSnap = JSON.parse(raw);
                applySnapshot(localSnap);
              }
            } catch {}
          }
        }
      } catch (e) {
        // Best effort restore
        console.warn('Autosave restore skipped:', e?.message || e);
        // Try local fallback on failure
        try {
          const raw = localStorage.getItem(LOCAL_KEY);
          if (raw) {
            const localSnap = JSON.parse(raw);
            applySnapshot(localSnap);
          }
        } catch {}
      }
    }
    loadAutosave();
    return () => { cancelled = true; };
  }, [isAuthenticated, authenticatedFetch, setLines, setActiveLineId, applySnapshot]);

  // ── Local restore (when not authenticated) ──
  useEffect(() => {
    if (isAuthenticated) { localHydratedRef.current = true; return; }
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (!raw) { localHydratedRef.current = true; return; }
      const saved = JSON.parse(raw);
      applySnapshot(saved);
    } catch (e) {
      // ignore parse errors
    } finally {
      localHydratedRef.current = true;
    }
  }, [isAuthenticated, setLines, setUnplacedByLine, setActiveLineId, applySnapshot]);

  // Keep the map in sync if lines ever change
  useEffect(() => {
    setUnplacedByLine((prev) => {
      const next = { ...prev };
      lines.forEach((l) => {
        if (!next[l.id]) next[l.id] = [];
      });
      return next;
    });
  }, [lines]);

  // Report aggregate unplaced count upward when it changes
  useEffect(() => {
    if (!onUnplacedChange) return;
    try {
      const total = Object.values(unplacedByLine).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
      onUnplacedChange(total);
    } catch (e) {
      // silent
    }
  }, [unplacedByLine, onUnplacedChange]);

  // ── Only the current line’s unplaced pallets ──
  const unplacedPallets = unplacedByLine[activeLineId] || [];
  const setUnplacedPallets = useCallback(
    (updaterOrValue) => {
      const isFn = typeof updaterOrValue === 'function';
      setUnplacedByLine((prev) => ({
        ...prev,
        [activeLineId]: isFn ? updaterOrValue(prev[activeLineId] || []) : updaterOrValue,
      }));
    },
    [activeLineId, setUnplacedByLine]
  );

  // Mark hydrated whether or not we restored anything to prevent initial save from clobbering
  hydratedRef.current = true;
  // ── Local 3D‐scene state ──
  // (moved above for safe references)

  // ── Debounced persist to backend autosave when state changes ──
  useEffect(() => {
    if (!isAuthenticated) return;
    // Don’t save until we’ve attempted to hydrate from server to avoid overwriting existing state
    if (!hydratedRef.current) return;
    // debounce saves to avoid spamming server
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const payload = {
        ...(lastAutosaveRef.current || {}),
        // namespace our UI state to avoid clashing with other parts of the app
        ml_lines: lines,
        ml_unplacedByLine: unplacedByLine,
        ml_activeLineId: activeLineId,
        ml_activeTruckIndex: activeTruckIndex,
        ml_ts: Date.now(),
      };
      try {
        await apiService.saveAutosave(payload);
        lastAutosaveRef.current = payload;
      } catch (e) {
        console.warn('Autosave save failed:', e?.message || e);
      }
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [isAuthenticated, lines, unplacedByLine, activeLineId, activeTruckIndex]);

  // ── Debounced persist to localStorage when not authenticated ──
  useEffect(() => {
    // Always mirror locally to guard against refreshes before server save lands
    if (!localHydratedRef.current) return; // avoid clobbering before we attempt local restore
    const t = setTimeout(() => {
      try {
        const payload = {
          ml_lines: lines,
          ml_unplacedByLine: unplacedByLine,
          ml_activeLineId: activeLineId,
          ml_activeTruckIndex: activeTruckIndex,
          ml_ts: Date.now(),
        };
        localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore storage quota errors
      }
    }, 250);
    return () => clearTimeout(t);
  }, [lines, unplacedByLine, activeLineId, activeTruckIndex]);

  // ── Immediate flush on tab hide/close to reduce data loss on refresh ──
  useEffect(() => {
    function flushLocal() {
      try {
        const payload = {
          ml_lines: lines,
          ml_unplacedByLine: unplacedByLine,
          ml_activeLineId: activeLineId,
          ml_activeTruckIndex: activeTruckIndex,
          ml_ts: Date.now(),
        };
        localStorage.setItem(LOCAL_KEY, JSON.stringify(payload));
      } catch {}
    }
    const onBeforeUnload = () => {
      flushLocal();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushLocal();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [lines, unplacedByLine, activeLineId, activeTruckIndex]);

  // Defensive: surface clearer error if import failed at runtime
  if (!TruckLoadingScene) {
    console.error("MultiLineTruckManager: TruckLoadingScene import is undefined. Check default export and import path ../TruckLoadingScene.");
    return (
      <div style={{ padding: 16, color: '#b91c1c', background: '#fee2e2' }}>
        Failed to load 3D scene component. Please check the console for details.
      </div>
    );
  }

  return (
    <TruckLoadingScene
      ref={ref}
      // ── multi-line props ──
      lines={lines}
  setLines={setLines}
      activeLineId={activeLineId}
      setActiveLineId={(id) => {
        setActiveLineId(id);
        setActiveTruckIndex(0);
      }}
      // ── per-line trucks ──
      trucks={truckList}
      setTrucks={updateLineTrucks}
      activeTruckIndex={activeTruckIndex}
      setActiveTruckIndex={setActiveTruckIndex}
      // ── per-line unplaced pallets ──
      unplacedPallets={unplacedPallets}
      setUnplacedPallets={setUnplacedPallets}
      // ── row weights callback ──
  onRowWeightsUpdate={onRowWeightsUpdate}
  onSelectedPalletChange={onSelectedPalletChange}
  />
  );
});

export default MultiLineTruckManager;
